/**
 * Error Handling Utilities for Payments Feature
 *
 * Provides structured error types, logging, and user-friendly messaging
 * following .claude/error-handling-rules.md standards
 */

/**
 * Structured Error Types
 * Hierarchy: CardError, ValidationError, NetworkError, StripeError
 */

interface ErrorDetails {
  [key: string]: unknown;
}

interface StripeErrorObject {
  type?: string;
  code?: string;
  message?: string;
  decline_code?: string;
  param?: string;
  statusCode?: number;
}

interface ApiErrorResponse {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
  message?: string;
}

interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  [key: string]: unknown;
}

export class PaymentError extends Error {
  type: string;
  code: string | number | null;
  details: ErrorDetails | null;
  timestamp: string;

  constructor(message: string, type: string = 'generic', code: string | number | null = null, details: ErrorDetails | null = null) {
    super(message);
    this.name = 'PaymentError';
    this.type = type;
    this.code = code;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

export class CardError extends PaymentError {
  constructor(message: string, code: string | null = null, details: ErrorDetails | null = null) {
    super(message, 'card_error', code, details);
    this.name = 'CardError';
  }
}

export class ValidationError extends PaymentError {
  constructor(message: string, field: string | null = null, details: ErrorDetails | null = null) {
    super(message, 'validation_error', field, details);
    this.name = 'ValidationError';
  }
}

export class NetworkError extends PaymentError {
  constructor(message: string, statusCode: number | null = null, details: ErrorDetails | null = null) {
    super(message, 'network_error', statusCode, details);
    this.name = 'NetworkError';
  }
}

export class StripeError extends PaymentError {
  constructor(message: string, stripeCode: string | null = null, details: ErrorDetails | null = null) {
    super(message, 'stripe_error', stripeCode, details);
    this.name = 'StripeError';
  }
}

/**
 * Map Stripe error types to structured error classes
 *
 * @param error - Stripe error object
 * @returns Structured error instance
 */
export function parseStripeError(error: StripeErrorObject | null): PaymentError {
  if (!error) {
    return new PaymentError('An unexpected error occurred');
  }

  const errorType = error.type;
  const errorCode = error.code;
  const errorMessage = error.message || 'An unexpected error occurred';

  switch (errorType) {
    case 'card_error':
      return new CardError(errorMessage, errorCode || null, {
        declineCode: error.decline_code,
        param: error.param,
      });

    case 'validation_error':
      return new ValidationError(errorMessage, error.param || null, {
        param: error.param,
      });

    case 'api_error':
    case 'api_connection_error':
      return new NetworkError(errorMessage, error.statusCode || null, {
        type: errorType,
      });

    case 'authentication_error':
    case 'idempotency_error':
    case 'invalid_request_error':
    case 'rate_limit_error':
      return new StripeError(errorMessage, errorCode || null, {
        type: errorType,
      });

    default:
      return new PaymentError(errorMessage, errorType || 'unknown', errorCode || null, {
        originalType: errorType,
      });
  }
}

/**
 * User-Friendly Error Messages
 * Maps technical errors to human-readable messages
 * Following error-handling-rules.md guidelines
 */
export const ERROR_MESSAGES = {
  // Network errors
  networkError: "Unable to connect. Please check your internet connection and try again.",
  timeout: "Request timed out. Please try again.",

  // Payment errors
  paymentFailed: "Payment could not be processed. Please check your payment details and try again.",
  cardDeclined: "Your card was declined. Please use a different payment method or contact your bank.",
  insufficientFunds: "Your card has insufficient funds. Please use a different payment method.",
  expiredCard: "Your card has expired. Please use a different payment method.",
  incorrectCvc: "The security code (CVC) is incorrect. Please check and try again.",
  processingError: "An error occurred while processing your payment. Please try again.",

  // Validation errors
  invalidEmail: "Please enter a valid email address.",
  invalidPhone: "Please enter a valid phone number.",
  invalidPostalCode: "Please enter a valid postal code.",
  missingField: "Please fill in all required fields.",

  // Cart/Intent errors
  cartEmpty: "Your cart is empty.",
  intentCreationFailed: "Unable to prepare checkout. Please try again.",
  intentUpdateFailed: "Unable to update payment details. Please try again.",

  // Generic errors
  genericError: "Something went wrong. Please try again later or contact support if the problem persists.",
  alreadyPurchased: "You've already purchased this item.",
} as const;

/**
 * Get user-friendly error message based on error type and code
 *
 * @param error - Error instance
 * @returns User-friendly error message
 */
export function getUserFriendlyMessage(error: PaymentError | Error | null): string {
  if (!error) {
    return ERROR_MESSAGES.genericError;
  }

  // Handle structured PaymentError instances
  if (error instanceof CardError) {
    const declineCode = error.details?.declineCode;

    switch (declineCode) {
      case 'insufficient_funds':
        return ERROR_MESSAGES.insufficientFunds;
      case 'expired_card':
        return ERROR_MESSAGES.expiredCard;
      case 'incorrect_cvc':
        return ERROR_MESSAGES.incorrectCvc;
      default:
        return ERROR_MESSAGES.cardDeclined;
    }
  }

  if (error instanceof ValidationError) {
    const field = error.code;

    switch (field) {
      case 'email':
        return ERROR_MESSAGES.invalidEmail;
      case 'phone':
        return ERROR_MESSAGES.invalidPhone;
      case 'postal_code':
        return ERROR_MESSAGES.invalidPostalCode;
      default:
        return ERROR_MESSAGES.missingField;
    }
  }

  if (error instanceof NetworkError) {
    return ERROR_MESSAGES.networkError;
  }

  if (error instanceof StripeError) {
    return ERROR_MESSAGES.processingError;
  }

  // Fallback: use error message if it looks user-friendly
  // (doesn't contain technical terms like "undefined", "null", "Error:")
  const message = error.message || '';
  const isTechnical = /undefined|null|Error:|TypeError|at \w+\./i.test(message);

  if (!isTechnical && message.length > 0 && message.length < 200) {
    return message;
  }

  return ERROR_MESSAGES.genericError;
}

/**
 * Log error with context for debugging
 * Follows error-handling-rules.md logging patterns
 *
 * @param error - Error to log
 * @param context - Additional context (component, action, userId, etc.)
 */
export function logPaymentError(error: Error | PaymentError, context: LogContext = {}): void {
  const errorData: Record<string, unknown> = {
    message: error.message,
    type: (error as PaymentError).type || error.name,
    code: (error as PaymentError).code,
    timestamp: (error as PaymentError).timestamp || new Date().toISOString(),
    ...context,
  };

  // Include details for structured errors
  if (error instanceof PaymentError) {
    errorData.details = error.details;
  }

  // Include stack trace in development
  if (process.env.NODE_ENV !== 'production') {
    errorData.stack = error.stack;
  }

  console.error('Payment Error:', errorData);

  // TODO: Send to Sentry or monitoring service in production
  // if (process.env.NODE_ENV === 'production' && window.Sentry) {
  //   window.Sentry.captureException(error, {
  //     tags: {
  //       feature: 'payments',
  //       component: context.component,
  //     },
  //     extra: errorData,
  //   });
  // }
}

/**
 * Handle API response errors and convert to structured errors
 *
 * @param error - API error response
 * @returns Structured error instance
 */
export function parseApiError(error: ApiErrorResponse): PaymentError {
  const status = error.response?.status;
  const message = error.response?.data?.message || error.message;

  switch (status) {
    case 400:
      return new ValidationError(message || 'Invalid request. Please check your input.');

    case 401:
    case 403:
      return new PaymentError(
        'Session expired. Please log in again.',
        'authentication_error',
        status
      );

    case 404:
      return new PaymentError(
        'The requested resource was not found.',
        'not_found',
        status
      );

    case 409:
      return new PaymentError(
        'This action conflicts with existing data.',
        'conflict',
        status
      );

    case 429:
      return new NetworkError('Too many requests. Please try again later.', status);

    case 500:
    case 502:
    case 503:
      return new NetworkError('Server error. Please try again later.', status);

    default:
      return new NetworkError(message || 'An unexpected error occurred.', status || null);
  }
}

interface LineItem {
  price: number | string;
  title: string;
  [key: string]: unknown;
}

/**
 * Validate cart items for common errors
 *
 * @param lineItems - Cart line items
 * @throws {ValidationError} If validation fails
 */
export function validateCartItems(lineItems: LineItem[] | null | undefined): void {
  if (!lineItems || !Array.isArray(lineItems)) {
    throw new ValidationError('Invalid cart data', 'lineItems');
  }

  if (lineItems.length === 0) {
    throw new ValidationError('Cart is empty', 'lineItems');
  }

  lineItems.forEach((item, index) => {
    if (!item.price || (typeof item.price !== 'number' && typeof item.price !== 'string')) {
      throw new ValidationError(
        `Invalid price for item at index ${index}`,
        `lineItems[${index}].price`,
        { item }
      );
    }

    if (!item.title) {
      throw new ValidationError(
        `Missing title for item at index ${index}`,
        `lineItems[${index}].title`,
        { item }
      );
    }
  });
}

/**
 * Validate currency code
 *
 * @param currency - Currency code (e.g., "GBP", "USD", "ZAR")
 * @throws {ValidationError} If invalid
 */
export function validateCurrency(currency: string | null | undefined): void {
  const validCurrencies = ['GBP', 'USD', 'EUR', 'ZAR'];

  if (!currency || typeof currency !== 'string') {
    throw new ValidationError('Invalid currency', 'currency');
  }

  const upperCurrency = currency.toUpperCase();

  if (!validCurrencies.includes(upperCurrency)) {
    throw new ValidationError(
      `Unsupported currency: ${currency}`,
      'currency',
      { currency }
    );
  }
}


/**
 * Check if an error value contains a meaningful, displayable message.
 *
 * WHY: Backend/Stripe errors sometimes arrive as undefined, null, or
 * stringified objects. Displaying these to users is unhelpful and confusing.
 * Centralised here so every consumer uses the same filter.
 */
const USELESS_ERROR_STRINGS = new Set(['undefined', 'null', '[object Object]', '']);
export function isValidErrorMessage(error: unknown): error is string {
  if (!error) return false;
  if (typeof error !== 'string') return false;
  return !USELESS_ERROR_STRINGS.has(error);
}

/**
 * Extract a human-readable message from an unknown error shape.
 *
 * Handles: Error instances, plain strings, {message: string} objects,
 * and falls back to a generic message for anything else.
 */
export function extractErrorMessage(err: unknown, fallback = 'An unexpected error occurred. Please try again.'): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object' && 'message' in err && typeof (err as any).message === 'string') {
    return (err as any).message;
  }
  return fallback;
}
